import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import { AuthProvider } from './contexts/AuthContext';
import { RequireAuth } from './components/RequireAuth';
import { AppShellWithOverlays } from './components/layout/AppShellWithOverlays';
import ErrorBoundary from './components/ErrorBoundary';
import { ToastProvider } from './components/ui/ToastProvider';
import Placeholder from './screens/_placeholders/Placeholder';
import { getCurrentDashboardBasePath } from './utils/dashboardBasePath';

// Route-based code splitting (post-v1.4): each screen becomes its own chunk,
// so the initial bundle only ships the shell (providers, layout, auth gate) and
// the currently-rendered screen. Vendor chunks (react-vendor, charts, dnd) are
// still split via vite.config.js `manualChunks` — untouched here.
const Login        = lazy(() => import('./screens/Login'));
const Overview     = lazy(() => import('./screens/Overview'));
const Sessions     = lazy(() => import('./screens/Sessions'));
const AIConsultant = lazy(() => import('./screens/AIConsultant'));
const Settings     = lazy(() => import('./screens/Settings'));
const Domains      = lazy(() => import('./screens/Domains'));
const Integrations = lazy(() => import('./screens/Integrations'));
const Embed        = lazy(() => import('./screens/Embed'));
const Billing      = lazy(() => import('./screens/Billing'));
const Quiz                = lazy(() => import('./screens/Quiz'));
const QuizPublicationPage = lazy(() => import('./screens/Quiz/QuizPublicationPage'));
const Clients             = lazy(() => import('./screens/Clients'));
const Presets             = lazy(() => import('./screens/Presets'));

// Minimal splash while a route chunk is streaming in. Keeps centered
// "Загружаем…" copy consistent with RequireAuth's loading state, and relies
// on tokens that are already in the base CSS bundle (no extra CSS chunk).
function ScreenFallback() {
  return (
    <div
      className="lk-page"
      style={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}
      aria-busy="true"
      aria-label="Загрузка раздела"
    >
      <div className="lk-text-muted">Загружаем…</div>
    </div>
  );
}

// Convenience wrapper: each route element gets its own ErrorBoundary
// so a render-error inside one screen doesn't take the whole AppShell down.
function Guarded({ children }) {
  return <ErrorBoundary>{children}</ErrorBoundary>;
}

// App-level composition:
//   BrowserRouter(basename=current dashboard mount: "/dashboard" or "/dashboard-react")
//   └── AuthProvider
//       └── ThemeProvider
//           └── WorkspaceProvider
//               └── ToastProvider
//                   └── ErrorBoundary (top-level)
//                       └── Suspense (route-level lazy chunks)
//                           └── Routes (each screen wrapped in its own ErrorBoundary)
//                               ├── /login                         (public)
//                               └── <RequireAuth><AppShellWithOverlays />  (protected)
//                                   — overlays = CommandPalette + TweaksPanel (M4-B)
export default function App() {
  return (
    <BrowserRouter basename={getCurrentDashboardBasePath()}>
      <AuthProvider>
        <ThemeProvider>
          <WorkspaceProvider>
            <ToastProvider>
              <ErrorBoundary>
                <Suspense fallback={<ScreenFallback />}>
                  <Routes>
                    <Route path="login" element={<Guarded><Login /></Guarded>} />
                    <Route
                      element={
                        <RequireAuth>
                          <AppShellWithOverlays />
                        </RequireAuth>
                      }
                    >
                      <Route index element={<Navigate to="/overview" replace />} />
                      <Route path="overview"        element={<Guarded><Overview /></Guarded>} />
                      <Route path="sessions"        element={<Guarded><Sessions /></Guarded>} />
                      <Route path="sessions/:id"    element={<Guarded><Sessions /></Guarded>} />
                      <Route path="ai-consultant"   element={<Guarded><AIConsultant /></Guarded>} />
                      <Route path="settings"        element={<Guarded><Settings /></Guarded>} />
                      <Route path="domains"         element={<Guarded><Domains /></Guarded>} />
                      <Route path="integrations"    element={<Guarded><Integrations /></Guarded>} />
                      <Route path="embed"           element={<Guarded><Embed /></Guarded>} />
                      <Route path="billing"         element={<Guarded><Billing /></Guarded>} />
                      <Route path="quiz"            element={<Guarded><Quiz /></Guarded>} />
                      <Route path="quiz/:quizId/publication" element={<Guarded><QuizPublicationPage /></Guarded>} />
                      <Route path="presets"         element={<Guarded><Presets /></Guarded>} />
                      <Route path="clients"         element={<Guarded><Clients /></Guarded>} />
                      <Route path="*"               element={<Placeholder name="Not Found" />} />
                    </Route>
                  </Routes>
                </Suspense>
              </ErrorBoundary>
            </ToastProvider>
          </WorkspaceProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
